/**
 * The miniature a tour step shows: the signed-in person's own console, drawn
 * with the console's real classes at a fixed virtual size and scaled down to
 * the dialog. Their brand colour, their menu, their controls; everything not
 * being talked about is blurred. Plain `createElement` (no JSX) so the node
 * test runner can load it; the icon drawing is injected (`renderIcon`)
 * because `icons.tsx` is JSX — `TourLayoutView` in tour-layout-view.tsx
 * supplies the real one.
 *
 * Accessibility: the wrapper is one `role="img"` named by `alt`; the stage
 * inside is `inert` and `aria-hidden`, so none of it can be focused, clicked
 * or read out.
 */
import {
  createElement,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';

import { accentTokens } from './accent.ts';
import type { IconName } from './icons.tsx';

export interface TourLayoutMenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon: IconName;
  readonly children?: readonly { readonly id: string; readonly label: string }[];
}
export type TourLayoutMenuEntry = TourLayoutMenuItem | { readonly section: string };
export interface TourLayout {
  /** `colour` null: the console's own accent token. */
  readonly brand: { readonly name: string; readonly colour: string | null };
  readonly user: { readonly name: string; readonly role: string };
  readonly menu: readonly TourLayoutMenuEntry[];
  /** Ids drawn sharp and highlighted; `['*']` = all sharp, none highlighted. */
  readonly focus: readonly string[];
  /** Main-area heading. */
  readonly title?: string;
  /** The step's real controls, drawn sharp and inert. */
  readonly content?: ReactNode;
  /** What the miniature shows, in the user's language. */
  readonly alt: string;
}

/** The virtual size the stage is laid out at before it is scaled. */
export const TOUR_LAYOUT_WIDTH = 960;
export const TOUR_LAYOUT_HEIGHT = 600;
/** Used until the box has been measured. */
const FALLBACK_SCALE = 0.6;

const h = createElement;

/** The brand's first letter, upper-cased (whole code point, not half a pair). */
export function brandInitial(name: string): string {
  const first = Array.from(name.trim())[0];
  return first ? first.toUpperCase() : '·';
}

/** Both themes' accent variables for a brand colour; a bad hex draws nothing. */
function brandVars(colour: string | null): CSSProperties | null {
  if (colour === null || !/^#[0-9a-f]{6}$/i.test(colour)) return null;
  const t = accentTokens(colour);
  return {
    '--a': t.light.a,
    '--a-deep': t.light.aDeep,
    '--a-ink': t.light.aInk,
    '--a-wash': t.light.aWash,
    '--ad': t.dark.ad,
    '--ad-deep': t.dark.adDeep,
    '--ad-ink': t.dark.adInk,
    '--ad-wash': t.dark.adWash,
  } as CSSProperties;
}

interface Group {
  readonly label: string | null;
  readonly items: TourLayoutMenuItem[];
}

function groupMenu(menu: readonly TourLayoutMenuEntry[]): Group[] {
  const groups: { label: string | null; items: TourLayoutMenuItem[] }[] = [];
  for (const entry of menu) {
    if ('section' in entry) groups.push({ label: entry.section, items: [] });
    else {
      let g = groups.at(-1);
      if (!g) groups.push((g = { label: null, items: [] }));
      g.items.push(entry);
    }
  }
  return groups.filter((g) => g.items.length > 0 || g.label !== null);
}

/** Measures the box and returns the scale that fits the stage to it. */
function useFitScale() {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(FALLBACK_SCALE);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / TOUR_LAYOUT_WIDTH);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, scale };
}

export function TourLayoutFrame({
  layout,
  renderIcon,
}: {
  layout: TourLayout;
  renderIcon: (name: IconName) => ReactNode;
}): ReactElement {
  const { ref, scale } = useFitScale();
  const rail = useRef<HTMLDivElement>(null);
  const all = layout.focus.includes('*');
  /*
   * A long menu runs past the stage's foot, and the step's own item may be
   * one of those (the affiliate portal's Platform tour entry is). Scroll the
   * miniature's rail so the first highlighted item sits in view; nothing to
   * do when it already does, or when every item is lit.
   */
  const focusKey = layout.focus.join('|');
  useEffect(() => {
    const el = rail.current;
    if (!el || all) return;
    const lit = el.querySelector<HTMLElement>('[aria-current="page"]');
    if (!lit) return;
    const bottom = lit.offsetTop + lit.offsetHeight;
    el.scrollTop = bottom > el.clientHeight ? Math.max(0, lit.offsetTop - el.clientHeight / 2) : 0;
  }, [focusKey, all]);
  const has = (id: string) => all || layout.focus.includes(id);
  const vars = brandVars(layout.brand.colour);

  const nav = (
    id: string,
    label: string,
    on: boolean,
    icon: ReactNode,
    extra?: string,
  ) =>
    h(
      'button',
      {
        key: id,
        type: 'button',
        tabIndex: -1,
        className: [extra, on ? undefined : 'is-blurred'].filter(Boolean).join(' ') || undefined,
        ...(on && !all ? { 'aria-current': 'page' as const } : {}),
      },
      icon,
      h('span', { className: 'nav-label' }, label),
    );

  const groups = groupMenu(layout.menu).map((g, gi) => {
    const items = g.items.map((item) => {
      const kids = item.children ?? [];
      const kidOn = (id: string) => has(id) || has(item.id);
      const itemOn = has(item.id) || kids.some((k) => has(k.id));
      return [
        nav(item.id, item.label, itemOn, renderIcon(item.icon)),
        ...kids.map((k) => nav(k.id, k.label, kidOn(k.id), null, 'tour-layout-child')),
      ];
    });
    const sectionOn = all || g.items.some((i) => has(i.id) || (i.children ?? []).some((k) => has(k.id)));
    return h(
      'div',
      { className: 'nav-group', key: `g${gi}` },
      g.label
        ? h('p', { className: sectionOn ? 'nav-group-label' : 'nav-group-label is-blurred' }, g.label)
        : null,
      items,
    );
  });

  const blocks = layout.content ? 2 : 3;

  const stage = h(
    'div',
    {
      className: vars ? 'tour-layout-stage has-brand' : 'tour-layout-stage',
      inert: true,
      'aria-hidden': 'true',
      style: { ...(vars ?? {}), '--s': String(scale), transform: `scale(${scale})` } as CSSProperties,
    },
    h(
      'div',
      { className: 'tour-layout-top' },
      h('span', { className: 'tour-layout-tile' }, brandInitial(layout.brand.name)),
      h('span', { className: 'tour-layout-brand' }, layout.brand.name),
      h('span', { className: 'tour-layout-user' }, layout.user.role ? `${layout.user.name} · ${layout.user.role}` : layout.user.name),
    ),
    h(
      'div',
      { className: 'tour-layout-body' },
      h('div', { className: 'tour-layout-rail', ref: rail }, h('div', { className: 'nav' }, groups)),
      h(
        'div',
        { className: 'tour-layout-main' },
        layout.title ? h('div', { className: 'page-head' }, h('h1', null, layout.title)) : null,
        layout.content ? h('div', { className: 'tour-layout-content' }, layout.content) : null,
        Array.from({ length: blocks }, (_, i) => h('div', { key: i, className: 'tour-layout-block is-blurred' })),
      ),
    ),
  );

  return h(
    'div',
    { className: 'tour-layout', ref, role: 'img', 'aria-label': layout.alt },
    stage,
  );
}
